import React, { useState, useRef, useEffect, useCallback, useContext } from 'react';
import { View, Text, TextInput, StyleSheet, Alert, ScrollView } from 'react-native';
import HeaderWithBack from '../../components/Header/headerWithBack';
import CustomPressable from '../../components/customPressable/customPressable';
import { useDispatch, useSelector } from 'react-redux';
import { CHAMBER_KEY, DOOR_KEY, DoorKeyContext } from '../../navigation/StackNavigator/doorKeyNavigator';
import { useTranslation } from 'react-i18next';
import { useCssStyle } from '../../customHook/getCssStyle';
import { validateOTP } from '../../Redux/Actions/doorKeyAction';
import AsyncStorage from '@react-native-async-storage/async-storage';
import FetchingDataScreen from '../../components/fetchingDataScreen';
import { setRenderFlashMessage } from '../../Redux/Actions/appConfigAction';
import { DOOR_KEY_CATEGORY } from '../../config/Constant';
import { sendAnalytics } from '../../Redux/Actions/analyticsAction';

const OtpScreen = ({ navigation }) => {
    const dispatch = useDispatch();
    const { widgetSessionId, widgetLaunchTime } = useContext(DoorKeyContext);
    const { webAppWidgetsInfo } = useSelector(state => state.deviceConfigSlice)
    const digitalKeyOtpLength = +webAppWidgetsInfo.appConfigs?.digitalKeyOtpLength || 4

    const { currentGuestDetails } = useSelector(state => state.guestDetailsSlice);
    const { guestDetailsList } = useSelector(state => state.guestDetailsSlice);
    const email = guestDetailsList[0]?.guestProfile?.email;
    const [otp, setOtp] = useState('');
    const [loading, setLoading] = useState(false);
    const inputRefs = useRef([]);
    let reservationNumber = currentGuestDetails?.reservationNumber;
     let locationId = currentGuestDetails?.location


    const getStyles = useCssStyle();

    const { t } = useTranslation();


    // Handle OTP Validation
    const handleValidateOTP = useCallback(async () => {

        if (otp.length != digitalKeyOtpLength) {
            dispatch(setRenderFlashMessage(true, { type: "danger", message: t('doorkey.invalid_otp_length') }));
            return;
        }

        try {
            setLoading(true);
            // Dispatch the validateOTP action
            dispatch(validateOTP(reservationNumber, locationId, otp, async (status, message) => {
                if (status) {
                    dispatch(sendAnalytics(DOOR_KEY_CATEGORY, "OTP VERIFICATION - SUCCESS", { "element": "widget", "widgetSessionId": widgetSessionId, "pageName": "OTP VERIFICATION"}));

                    await AsyncStorage.setItem('doorkeyValidation', JSON.stringify(status));
                    navigation.reset({
                        index: 0,
                        routes: [{ name: DOOR_KEY }],
                    });
                } else {
                    dispatch(setRenderFlashMessage(true, { type: "danger", message: message || "Something went wrong" }))
                    dispatch(sendAnalytics(DOOR_KEY_CATEGORY, "OTP VERIFICATION - FAILED", { "element": "widget", "widgetSessionId": widgetSessionId, "pageName": "OTP VERIFICATION", "errorMessage": message || "Error while calling digital key api: validateOTP"}));
                }
                setLoading(false);
            }));
        } catch (error) {
            console.error('Error validating OTP:', error);
            // Alert.alert("Error", "An error occurred while validating the OTP.", [{ text: "OK" }]);
            dispatch(setRenderFlashMessage(true, { type: "danger", message: t('doorkey.error_validating_otp') }));
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [otp, navigation, locationId, dispatch, reservationNumber, widgetSessionId]);

    const maskEmail = (email) => {
        if (!email) return '';
        const [localPart, domain] = email.split('@');
        const maskedLocalPart = localPart.length > 2 ? localPart[0] + '****' : localPart;
        const maskedDomain = domain ? domain.replace(/(.{3}).*$/, '$1****') : '';
        return `${maskedLocalPart}@${maskedDomain}`;
    };

    // Handle OTP input change
    const handleInputChange = (text, index) => {
        const otpArray = otp.split('');
        otpArray[index] = text;
        setOtp(otpArray.join(''));

        // Move focus to the next input
        if (text && index < inputRefs.current.length - 1) {
            inputRefs.current[index + 1].focus();
        }
    };

    // Handle key press for backspace (move focus to the previous input if needed)
    const handleKeyPress = (e, index) => {
        if (e.nativeEvent.key === 'Backspace' && !otp[index] && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    return (
        <View style={getStyles(['widgetRootContainer', 'otpRootContainer'])}>
            {loading && <FetchingDataScreen loaderMessage={t("doorkey.validatingOTP")} />}
            <HeaderWithBack />
            <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={getStyles(['widgetmainContainer', 'doorkeyMainContainer'])}>
                <View style={getStyles(['widgetmainContainerHeaderContainer', 'doorkeyMainContainerHeaderContainer'])}>
                    <View style={[getStyles(['widgetmainContainerHeaderTitleContainer', 'otpMainContainerHeaderTitleContainer'])]}>

                        <Text style={getStyles(['widgetmainContainerHeaderTitle', 'otpMainContainerHeaderTitle'])}> {t('doorkey.almost_there')}</Text>
                        <Text style={getStyles(['widgetmainContainerHeaderSubTitle', 'otpMainContainerHeaderSubTitle'])}>
                            {t('doorkey.activate_room')},{' '}
                            <Text style={getStyles(['doorKeyOtpEmailTxt'])}>{maskEmail(email)}</Text>.
                        </Text>
                    </View>
                    <View style={getStyles(['widgetmainContainerChildContainer', 'otpMainContainerChildContainer'])}>
                        <Text style={getStyles(['doorKeyOtpPinLabel'])}>{t('doorkey.one_time_pin')}</Text>

                        {/* OTP Input */}
                        <View style={getStyles(['doorKeyOtpPinContainer'])}>
                            {Array(digitalKeyOtpLength).fill().map((_, index) => (
                                <OtpInput
                                    key={index}
                                    ref={(el) => (inputRefs.current[index] = el)}
                                    index={index}
                                    value={otp[index] || ''}
                                    onChangeText={handleInputChange}
                                    onKeyPress={(e) => handleKeyPress(e, index)}
                                    getStyles={getStyles}
                                />
                            ))}
                        </View>

                        {/* Activate Room Key Button */}
                        <CustomPressable style={getStyles(['primaryBtn'])} onPress={handleValidateOTP}>
                            <Text style={getStyles(['primaryBtnTxt'])}>{t('doorkey.activate_room_key')}</Text>
                        </CustomPressable>
                    </View>
                </View>
            </ScrollView>
        </View>
    );
};

// Reusable OTP Input Component
const OtpInput = React.forwardRef(({ index, value, onChangeText, onKeyPress, getStyles }, ref) => {
    return (
        <TextInput
            ref={ref}
            style={getStyles(['doorKeyOtpPinInput'])}
            maxLength={1}
            keyboardType="numeric"
            value={value}
            onChangeText={(text) => onChangeText(text, index)}
            onKeyPress={onKeyPress}
        />
    );
});



export default OtpScreen;
